"use client"

import React, { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Loader2, Trash2 } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import axios from "axios"

export function ExternalUserModal({
  open,
  onOpenChange,
  user,
  integrations = [],
  canEdit = false,
}) {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [externalUsers, setExternalUsers] = useState([])
  const [mappingToDelete, setMappingToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [formData, setFormData] = useState({
    integration_id: "",
    external_id: "",
  })

  const resetForm = () => {
    setFormData({
      integration_id: integrations[0]?.id?.toString() ?? "",
      external_id: "",
    })
  }

  const fetchExternalUsers = async () => {
    if (!user?.id) return

    try {
      setIsLoading(true)
      const response = await axios.get(`/users/${user.id}/external-users`)
      setExternalUsers(response.data || [])
    } catch (error) {
      console.error("Error fetching external users:", error)
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "Failed to load external user mappings.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (open && user?.id) {
      resetForm()
      fetchExternalUsers()
    }
  }, [open, user?.id])

  useEffect(() => {
    if (open && integrations.length > 0 && !formData.integration_id) {
      setFormData((prev) => ({
        ...prev,
        integration_id: integrations[0].id.toString(),
      }))
    }
  }, [open, integrations, formData.integration_id])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canEdit || !user?.id) return

    if (!formData.integration_id) {
      toast({
        title: "Validation error",
        description: "Please select an integration.",
        variant: "destructive",
      })
      return
    }

    if (!formData.external_id.trim()) {
      toast({
        title: "Validation error",
        description: "External ID is required.",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)

    try {
      await axios.post(`/users/${user.id}/external-users`, {
        integration_id: Number(formData.integration_id),
        external_id: formData.external_id.trim(),
      })

      toast({
        title: "Success",
        description: "External ID mapping saved successfully.",
      })

      resetForm()
      await fetchExternalUsers()
    } catch (error) {
      console.error("Error saving external user mapping:", error)
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to save external ID mapping.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!mappingToDelete || !user?.id) return

    setIsDeleting(true)

    try {
      await axios.delete(
        `/users/${user.id}/external-users/${mappingToDelete.id}`
      )

      toast({
        title: "Deleted",
        description: "External ID mapping removed.",
      })

      setMappingToDelete(null)
      await fetchExternalUsers()
    } catch (error) {
      console.error("Error deleting external user mapping:", error)
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "Failed to delete external ID mapping.",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const userLabel = user
    ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email
    : ""

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-10/12 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>External IDs{userLabel ? ` — ${userLabel}` : ""}</DialogTitle>
            <DialogDescription>
              Map this user to their ID in external CRM systems for workflow
              integrations.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Existing mappings</h4>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : externalUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No external IDs configured.
                </p>
              ) : (
                <div className="rounded-md border divide-y">
                  {externalUsers.map((mapping) => (
                    <div
                      key={mapping.id}
                      className="flex items-center justify-between gap-3 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {mapping.integration_name}
                        </div>
                        <div className="text-sm text-muted-foreground truncate">
                          ID: {mapping.external_id}
                        </div>
                      </div>
                      {canEdit && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setMappingToDelete(mapping)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete mapping</span>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {canEdit && (
              <form onSubmit={handleSubmit} className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-medium">Add or update mapping</h4>
                <div className="space-y-2">
                  <Label htmlFor="integration_id">Integration</Label>
                  <Select
                    value={formData.integration_id}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        integration_id: value,
                      }))
                    }
                    disabled={integrations.length === 0 || isSubmitting}
                  >
                    <SelectTrigger id="integration_id">
                      <SelectValue placeholder="Select integration" />
                    </SelectTrigger>
                    <SelectContent>
                      {integrations.map((integration) => (
                        <SelectItem
                          key={integration.id}
                          value={integration.id.toString()}
                        >
                          {integration.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {integrations.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      No CRM integrations are configured.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="external_id">External ID</Label>
                  <Input
                    id="external_id"
                    value={formData.external_id}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        external_id: e.target.value,
                      }))
                    }
                    placeholder="e.g. 2550"
                    disabled={isSubmitting}
                  />
                </div>
                <DialogFooter className="px-0">
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      integrations.length === 0 ||
                      !formData.integration_id
                    }
                    className="dark:text-white"
                  >
                    {isSubmitting && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save Mapping
                  </Button>
                </DialogFooter>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!mappingToDelete}
        onOpenChange={(isOpen) => {
          if (!isOpen) setMappingToDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete external ID mapping?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {mappingToDelete?.integration_name} mapping
              (ID: {mappingToDelete?.external_id}) for {userLabel}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
